/**
 * Tarih biçimi: tasarımdaki gibi `GG.AA.YYYY` (personel-sertifikalar.jpeg). Kayıtlardaki gün
 * `YYYY-MM-DD` gemi yerel tarihidir; saat dilimi kaydırması yapılmaz (CLAUDE.md "Zaman").
 */
export function formatDay(date: string | null | undefined): string {
  if (!date) return '—';
  const [year, month, day] = date.split('-');
  return year && month && day ? `${day}.${month}.${year}` : date;
}

/** Ondalık saatten `S:DD` (ör. 13,25 → "13:15"); `null` → "—". */
export function formatHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || Number.isNaN(hours)) return '—';
  const minutes = Math.round(hours * 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Bir an: ISO-8601 (UTC), milisaniye ya da `Date`. */
export type Instant = string | number | Date;

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Anın takvim parçaları. `offsetMin` verilirse UTC + fark (gemi saati; kayıtlar UTC ve gemi saati
 * farkıyla tutulur, CLAUDE.md "Zaman"); verilmezse çalışma ortamının yerel saati.
 */
function parts(value: Instant | null | undefined, offsetMin: number | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const at = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (Number.isNaN(at)) return null;
  if (offsetMin === undefined) {
    const local = new Date(at);
    return {
      year: local.getFullYear(),
      month: local.getMonth() + 1,
      day: local.getDate(),
      hours: local.getHours(),
      minutes: local.getMinutes(),
    };
  }
  const shifted = new Date(at + offsetMin * 60_000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hours: shifted.getUTCHours(),
    minutes: shifted.getUTCMinutes(),
  };
}

/**
 * Tarih ve saat `GG.AA.YYYY SS:DD` (24 saat; M6f plan §0.6). İşletim sisteminin dil biçiminden
 * bağımsızdır. `offsetMin`: gemi saati farkı (dakika).
 */
export function formatDateTime(value: Instant | null | undefined, offsetMin?: number): string {
  const p = parts(value, offsetMin);
  if (!p) return '—';
  return `${pad(p.day)}.${pad(p.month)}.${p.year} ${pad(p.hours)}:${pad(p.minutes)}`;
}

/** Saat `SS:DD` (24 saat, AM/PM yok). `offsetMin`: gemi saati farkı (dakika). */
export function formatTime(value: Instant | null | undefined, offsetMin?: number): string {
  const p = parts(value, offsetMin);
  return p ? `${pad(p.hours)}:${pad(p.minutes)}` : '—';
}

/**
 * Uzun gün, dile göre: "04 Oct 2026 (Sun)", "04 Eki 2026 (Paz)". Yalnız referansın uzun yazdığı
 * gezinme başlıklarında (dinlenme saati gün çubuğu); tablo ve alanlarda `formatDay`.
 */
export function formatDayLong(date: string | null | undefined, language: string): string {
  if (!date) return '—';
  const at = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return date;
  const locale = language === 'en' ? 'en-GB' : language;
  const month = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
    .format(at)
    .replace('.', '');
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
    .format(at)
    .replace('.', '');
  return `${pad(at.getUTCDate())} ${month} ${at.getUTCFullYear()} (${weekday})`;
}

/** `YYYY-MM-DD` gerçek bir gün mü (31.04 ya da 29.02.2026 değil). */
function validDay(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1) return null;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > last) return null;
  return `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;
}

/**
 * Elle yazılan gün: `GG.AA.YYYY` (ayraç `.`, `/` ya da `-`; gün ve ay tek haneli olabilir),
 * ayraçsız `GGAAYYYY` ya da `YYYY-AA-GG`. Geçersizse `null`. Yıl dört hanelidir.
 */
export function parseDay(text: string): string | null {
  const value = text.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (iso) return validDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const dmy =
    /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(value) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(value);
  if (dmy) return validDay(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));
  return null;
}

/** Tarih-saat girişinin değeri (`YYYY-MM-DDTHH:mm`, yerel) → Mantine değeri (`YYYY-MM-DD HH:mm:ss`). */
export function toDateTimeValue(value: string | null | undefined): string | null {
  const match = value ? /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value) : null;
  return match ? `${match[1]} ${match[2]}:00` : null;
}

/** Mantine değeri (`YYYY-MM-DD HH:mm[:ss]`) → tarih-saat girişinin değeri (`YYYY-MM-DDTHH:mm`). */
export function fromDateTimeValue(value: string | null | undefined): string {
  const match = value ? /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(value) : null;
  return match ? `${match[1]}T${match[2]}` : '';
}
